/**
 * AI 가 바꾸려는 파일 이름의 **판정 규칙** — 순수 함수 (2026-08-15).
 *
 * ## 왜 따로 두는가
 *
 * 같은 규칙을 두 곳이 쓴다: 평문 카드의 `rename_file` 도구(`text-tools.ts`)와, GenOffice
 * 다섯이 지나는 호스트 창구(`WorkspaceDocPanel` 의 `renameFile`). 두 벌로 두면 한쪽만
 * 고쳐지는 날이 오고, 그때 사용자는 **편집기에 따라 되고 안 되는** 이름 바꾸기를 만난다.
 *
 * ## 사람과 AI 의 규칙이 다른 것은 의도다
 *
 * 제목 줄의 연필(사람)은 확장자까지 자유롭게 고친다 — 목록의 이름 바꾸기와 같은 규칙이고,
 * 보고 고치는 자리라 결과를 즉시 확인한다. AI 는 **확장자를 고정**한다: 확장자가 바뀌면
 * 이 파일을 여는 편집기·미리보기 판정(`previewKindOfNode`)이 통째로 달라져 "이름만
 * 고쳤는데 안 열린다" 가 되는데, 추측해서 고치는 쪽이 그 위험을 지면 안 된다.
 */

export type RenameNameCheck =
  | { ok: true; name: string }
  | { ok: false; reason: string };

/**
 * 새 이름을 확정한다.
 *
 * - 확장자를 **빼고** 주면 지금 확장자를 붙여 준다.
 * - **다른** 확장자를 주면 거절한다(조용히 고쳐 주면 모델이 자기가 뭘 했는지 모른다).
 * - 경로 구분자는 거절한다 — 이름 바꾸기는 이동이 아니다.
 */
export function resolveRenameName(current: string, requested: string): RenameNameCheck {
  const raw = requested.trim();
  if (!raw) return { ok: false, reason: 'name 이 비었습니다.' };
  if (/[\\/]/.test(raw)) {
    return { ok: false, reason: '이름에 경로 구분자(/ 또는 \\)를 넣을 수 없습니다. 파일 이름만 주세요.' };
  }

  const ext = extensionOf(current);
  const givenExt = extensionOf(raw);
  if (!givenExt) return { ok: true, name: ext ? `${raw}${ext}` : raw };
  if (ext && givenExt.toLowerCase() !== ext.toLowerCase()) {
    return {
      ok: false,
      reason: `확장자는 바꿀 수 없습니다(현재 "${ext}", 요청 "${givenExt}"). 확장자를 빼고 이름만 주세요.`,
    };
  }
  return { ok: true, name: raw };
}

/** 마지막 점 뒤 확장자(점 포함). 없으면 빈 문자열. 숨김파일(`.env`)은 확장자가 없는 것으로 본다. */
export function extensionOf(name: string): string {
  const at = name.lastIndexOf('.');
  return at > 0 ? name.slice(at) : '';
}
